import { useState, useCallback } from 'react';
import { getTodayDateString } from '@/shared/utils/dateUtils';
import { subDays, format } from 'date-fns';

/**
 * Date range presets for quick filtering
 */
export type DateRangePreset = 'last7' | 'last30' | 'last90' | 'custom';

export interface ReportFiltersState {
  dateFrom: string;
  dateTo: string;
  locationIds: string[];
  manufacturerIds: string[];
  datePreset: DateRangePreset;
}

/**
 * Hook to manage report filters state
 * Provides shared filter state across all report sections
 */
export function useReportFilters() {
  const today = getTodayDateString();
  const last30Days = format(subDays(new Date(), 30), 'yyyy-MM-dd');

  const [filters, setFilters] = useState<ReportFiltersState>({
    dateFrom: last30Days,
    dateTo: today,
    locationIds: [],
    manufacturerIds: [],
    datePreset: 'last30',
  });

  // Handlers for updating filters - memoized to prevent re-renders
  const setDatePreset = useCallback((preset: DateRangePreset) => {
    const today = getTodayDateString();
    
    setFilters((prev) => {
      let dateFrom = prev.dateFrom;

      switch (preset) {
        case 'last7':
          dateFrom = format(subDays(new Date(), 7), 'yyyy-MM-dd');
          break;
        case 'last30':
          dateFrom = format(subDays(new Date(), 30), 'yyyy-MM-dd');
          break;
        case 'last90':
          dateFrom = format(subDays(new Date(), 90), 'yyyy-MM-dd');
          break;
        case 'custom':
          // Keep current dates
          break;
      }

      return {
        ...prev,
        dateFrom,
        dateTo: today,
        datePreset: preset,
      };
    });
  }, []);

  const setDateFrom = useCallback((dateFrom: string) => {
    setFilters((prev) => ({
      ...prev,
      dateFrom,
      datePreset: 'custom',
    }));
  }, []);

  const setDateTo = useCallback((dateTo: string) => {
    setFilters((prev) => ({
      ...prev,
      dateTo,
      datePreset: 'custom',
    }));
  }, []);

  const setLocationIds = useCallback((locationIds: string[]) => {
    setFilters((prev) => {
      // Only update if actually different to prevent unnecessary re-renders
      const prevSorted = [...prev.locationIds].sort().join(',');
      const newSorted = [...locationIds].sort().join(',');
      if (prevSorted === newSorted) {
        return prev; // No change needed
      }
      return {
        ...prev,
        locationIds,
      };
    });
  }, []);

  const setManufacturerIds = useCallback((manufacturerIds: string[]) => {
    setFilters((prev) => {
      // Only update if actually different to prevent unnecessary re-renders
      const prevSorted = [...prev.manufacturerIds].sort().join(',');
      const newSorted = [...manufacturerIds].sort().join(',');
      if (prevSorted === newSorted) {
        return prev; // No change needed
      }
      return {
        ...prev,
        manufacturerIds,
      };
    });
  }, []);

  const resetFilters = useCallback(() => {
    const today = getTodayDateString();
    const last30Days = format(subDays(new Date(), 30), 'yyyy-MM-dd');

    setFilters({
      dateFrom: last30Days,
      dateTo: today,
      locationIds: [],
      manufacturerIds: [],
      datePreset: 'last30',
    });
  }, []);

  return {
    filters,
    setDatePreset,
    setDateFrom,
    setDateTo,
    setLocationIds,
    setManufacturerIds,
    resetFilters,
  };
}
